import { useContext } from 'react';
import BackgroundOverlay from '@/common/BackgroundOverlay';
import FeatureBranch from '@/common/FeatureBranch';
import PageFooter from '@/common/PageFooter';
import PageHeader from '@/common/PageHeader';
import InternalLinks from '@/home/InternalLinks';
import { StaticPromoTile, DynamicPromoTile } from '@/home/PromoTiles';
import { TileType } from '@/home/PromoTiles/TileUtils';
import RecommendedCarousels from '@/home/RecommendedCarousels';
import formatEvent from '@/home/utils/formatEvent';
import HideOnNative from '~/components/common/HideOnNative';
import TrackWrapper from '~/components/common/TrackWrapper';
import TradeContext, {
  useGetTradeInStatus
} from '~/context/common/TradeContext';
import { useIsLoggedIn } from '~/context/common/UserContext';
import FinanceContext, {
  useHasExpiredSoftpull,
  useHasValidSoftPull
} from '~/context/home/FinanceContext';
import { useGetRecentSearches } from '~/context/home/RecentSearchesContext';
import { useGetRecentVehiclesWithFavorites } from '~/context/home/RecentVehiclesContext';
import NativeError from '~/native/NativeError';
import AffordableCars from './AffordableCars';
import Hero from './Hero';
import {
  ExpandedSectionContent,
  PromoTileWrapper,
  SectionDark,
  SectionContent,
  SectionLight
} from './HomeContainer.styles';
import Modals from './Modals';
import NewPriceBanner from './NewPriceBanner';
import OrderStatusCardsWrapper from './OrderStatusCards';
import PopularBodyStyles from './PopularBodyStyles';
import PopularModels from './PopularModels';
import CVTBanner from './PromoCVT/CVTBanner';
import RecentlyViewed from './RecentlyViewed';
import RecentSearches from './RecentSearches';
import SuggestedForYou from './SuggestedForYou';

export default function HomeContainer() {
  const { trades } = useContext(TradeContext);
  const { financeInfo } = useContext(FinanceContext);
  const isLoggedIn = useIsLoggedIn();
  const recentVehicleViews = useGetRecentVehiclesWithFavorites();
  const recentSearches = useGetRecentSearches();
  const hasValidSoftpull = useHasValidSoftPull();
  const hasExpiredSoftpull = useHasExpiredSoftpull();
  const tradeInStatus = useGetTradeInStatus();

  const isReturningOrLoggedInUser =
    recentVehicleViews.length + recentSearches.length > 0 || isLoggedIn;

  const dynamicPromoAttributes = {
    description: 'Dual promo tile',
    content: ['Finance', 'Trade']
  };

  const staticPromoAttributes = {
    description: 'Dual promo tile',
    content: ['Value', 'Insurance']
  };

  const dynamicPromoViewEvent = formatEvent(
    'Viewed - Home feed section',
    dynamicPromoAttributes
  );
  const staticPromoViewEvent = formatEvent(
    'Viewed - Home feed section',
    staticPromoAttributes
  );

  return (
    <>
      <BackgroundOverlay />
      <HideOnNative>
        <PageHeader isLoggedIn={isLoggedIn} />
      </HideOnNative>
      <NativeError />
      <FeatureBranch appName="homepage" />
      <main>
        <Hero isReturningUser={isReturningOrLoggedInUser} />
        <SectionLight>
          <SectionContent>
            <CVTBanner />
            {!isReturningOrLoggedInUser && (
              <PopularBodyStyles
                isReturningOrLoggedInUser={isReturningOrLoggedInUser}
              />
            )}
          </SectionContent>

          {isReturningOrLoggedInUser && (
            <ExpandedSectionContent>
              <OrderStatusCardsWrapper />
              <RecentlyViewed />
              <RecentSearches recentSearches={recentSearches} />
              <SuggestedForYou />
            </ExpandedSectionContent>
          )}

          <SectionContent>
            <TrackWrapper event={dynamicPromoViewEvent}>
              <div className="mt-20">
                <PromoTileWrapper className="mb-40">
                  <DynamicPromoTile
                    tileType={TileType.Finance}
                    isActive={hasValidSoftpull}
                    isExpired={hasExpiredSoftpull}
                    isLoaded={financeInfo.isLoaded}
                    isNewUser={!isReturningOrLoggedInUser}
                  />
                  <DynamicPromoTile
                    tileType={TileType.Trade}
                    isActive={tradeInStatus.isActive}
                    isExpired={tradeInStatus.isExpired}
                    isLoaded={trades.isLoaded}
                    isPending={tradeInStatus.isPending}
                    isNewUser={!isReturningOrLoggedInUser}
                  />
                </PromoTileWrapper>
                <NewPriceBanner />
              </div>
            </TrackWrapper>
          </SectionContent>
        </SectionLight>

        <SectionDark className="s:pt-40 m:pt-[64px]">
          <SectionContent>
            {isReturningOrLoggedInUser && (
              <PopularBodyStyles
                isReturningOrLoggedInUser={isReturningOrLoggedInUser}
              />
            )}
            <AffordableCars />
          </SectionContent>
        </SectionDark>

        {isReturningOrLoggedInUser && (
          <SectionLight className="s:pt-40 m:pt-[64px]">
            <ExpandedSectionContent>
              <RecommendedCarousels />
            </ExpandedSectionContent>
          </SectionLight>
        )}

        <SectionDark className="s:pt-40 m:pt-[64px]">
          <SectionContent>
            <TrackWrapper event={staticPromoViewEvent}>
              <PromoTileWrapper>
                <StaticPromoTile type={TileType.Value} />
                <StaticPromoTile type={TileType.Insurance} />
              </PromoTileWrapper>
            </TrackWrapper>
          </SectionContent>
        </SectionDark>

        <SectionLight className="pt-40">
          <ExpandedSectionContent>
            <PopularModels />
          </ExpandedSectionContent>
          {!isReturningOrLoggedInUser && (
            <HideOnNative>
              <InternalLinks />
            </HideOnNative>
          )}
        </SectionLight>
      </main>
      <HideOnNative>
        <PageFooter />
      </HideOnNative>
      <Modals />
    </>
  );
}
